import { useState, useEffect, useRef } from "react";
import { SlidersHorizontal, X, CalendarDays, ChevronDown, Check, Search } from "lucide-react";
import { C } from "./constants";

// ─── Multi-select with optional search ───────────────────────────────────────
function FMultiSel({ values = [], onChange, options = [], placeholder = "Choose...", searchable = false }) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false); setQuery("");
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);


  const toggle = (val) =>
    onChange(values.includes(val) ? values.filter(v => v !== val) : [...values, val]);

  const filtered = searchable && query.trim()
    ? options.filter(o => (o.label || o).toString().toLowerCase().includes(query.toLowerCase()))
    : options;

  const label = values.length === 0 ? placeholder
    : values.length === 1 ? (options.find(o => (o.value || o) === values[0])?.label || values[0])
    : `${values.length} selected`;

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <div
        onClick={() => { setOpen(o => !o); setQuery(""); }}
        className={`flex items-center justify-between px-[13px] py-[10px] rounded-[10px] cursor-pointer bg-white select-none transition-colors duration-150 border-[1.5px] ${open ? "border-[#6366F1]" : "border-[#E8ECF0]"}`}
      >
        <span className={`text-[13px] overflow-hidden whitespace-nowrap text-ellipsis flex-1 ${values.length ? "text-[#1E293B]" : "text-[#94A3B8]"}`}>
          {label}
        </span>
        <ChevronDown size={12} color="#94A3B8" className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-[9999] bg-white border-[1.5px] border-[#E8ECF0] rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,.12)]">

          {/* Search bar */}
          {searchable && (
            <div className="px-[10px] py-2 border-b border-[#F1F5F9]">
              <div className="flex items-center gap-[7px] px-[10px] py-[6px] bg-[#F8FAFC] rounded-lg border border-[#E8ECF0]">
                <Search size={12} color="#94A3B8" className="shrink-0" />
                <input
                  autoFocus
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  placeholder="Search..."
                  className="flex-1 border-none outline-none text-[12px] text-[#1E293B] bg-transparent font-[inherit]"
                />
                {query && (
                  <span onClick={e => { e.stopPropagation(); setQuery(""); }} className="cursor-pointer text-[#94A3B8] leading-none">
                    <X size={10} />
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Options list */}
          <div className="max-h-[180px] overflow-y-auto scrollbar-thin">
            {filtered.length === 0 ? (
              <div className="px-[13px] py-[10px] text-[12px] text-[#94A3B8]">
                {query ? "No results found" : "No options"}
              </div>
            ) : filtered.map(opt => {
              const val = opt.value !== undefined ? opt.value : opt;
              const lbl = opt.label !== undefined ? opt.label : opt;
              const sel = values.includes(val);
              return (
                <div key={val} onClick={() => toggle(val)}
                  className={`flex items-center gap-2 px-[13px] py-[9px] cursor-pointer text-[13px] text-[#1E293B] transition-colors duration-100 ${sel ? "bg-[#EEF2FF]" : "hover:bg-[#F8FAFC]"}`}>
                  <div className={`w-4 h-4 rounded-[4px] shrink-0 flex items-center justify-center border-[1.5px] transition-all duration-150 ${sel ? "bg-[#6366F1] border-[#6366F1]" : "bg-white border-[#CBD5E1]"}`}>
                    {sel && <Check size={10} color="#fff" strokeWidth={3} />}
                  </div>
                  <span className="overflow-hidden whitespace-nowrap text-ellipsis">{lbl}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected chips */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-[5px] mt-[7px]">
          {values.map(v => {
            const lbl = options.find(o => (o.value || o) === v)?.label || v;
            return (
              <span key={v} onClick={() => toggle(v)}
                className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full bg-[#EEF2FF] text-[#4338CA] text-[11px] font-semibold cursor-pointer border border-[#C7D2FE]">
                {lbl}<X size={10} strokeWidth={2.5} />
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Date input ───────────────────────────────────────────────────────────────
function DInput({ value, onChange }) {
  return (
    <div className="flex items-stretch border-[1.5px] border-[#E8ECF0] rounded-[10px] overflow-hidden">
      <div className="bg-[#6366F1] px-3 flex items-center shrink-0">
        <CalendarDays size={14} color="#fff" />
      </div>
      <input type="date" value={value} onChange={onChange}
        className={`flex-1 border-none px-[10px] py-[10px] text-[12.5px] outline-none font-[inherit] bg-white w-full ${value ? "text-[#1E293B]" : "text-[#94A3B8]"}`}
      />
    </div>
  );
}

// ─── Label row ────────────────────────────────────────────────────────────────
function FRow({ label, children, last }) {
  return (
    <div className={last ? "" : "mb-[18px]"}>
      <div className="text-[12px] font-bold text-[#374151] mb-[6px]">{label}</div>
      {children}
    </div>
  );
}

// ─── Main FilterSidebar ───────────────────────────────────────────────────────
export default function FilterSidebar({ open, onClose, onApply, onClear, fv, setFv, filterOpts }) {
  const categoryOptions = (filterOpts?.categories || []).map(c =>
    typeof c === "string" ? { value: c, label: c } : { value: c.id || c.name, label: c.name }
  );
  const deptOptions = (filterOpts?.departments || []).map(d =>
    typeof d === "string" ? { value: d, label: d } : { value: d.id || d.name, label: d.name }
  );
  const priorityOptions = [
    { value: "CRITICAL", label: "Critical" },
    { value: "URGENT",   label: "Urgent"   },
    { value: "HIGH",     label: "High"     },
    { value: "MEDIUM",   label: "Medium"   },
    { value: "LOW",      label: "Low"      },
  ];

  return (
    <>
      {open && (
        <div onClick={onClose} className="fixed inset-0 bg-[rgba(15,23,42,.35)] z-[999] backdrop-blur-sm" />
      )}

      <div className={`fixed top-0 right-0 h-screen w-[320px] z-[1000] flex flex-col bg-white transition-transform duration-[280ms] ease-[cubic-bezier(.4,0,.2,1)] ${open ? "translate-x-0 shadow-[-4px_0_24px_rgba(15,23,42,.15)]" : "translate-x-full"}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-[22px] pb-[18px] border-b border-[#F1F5F9] shrink-0">
          <div className="flex items-center gap-[9px]">
            <div className="w-8 h-8 rounded-[9px] bg-[#EEF2FF] flex items-center justify-center">
              <SlidersHorizontal size={14} color={C.indigo} />
            </div>
            <span className="text-[16px] font-extrabold text-[#1E293B]">Filter</span>
          </div>
          <button onClick={onClose}
            className="w-[30px] h-[30px] border-none bg-transparent cursor-pointer rounded-lg flex items-center justify-center text-[#94A3B8] hover:bg-[#F1F5F9] transition-colors duration-150">
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 scrollbar-thin scrollbar-thumb-[#E2E8F0] scrollbar-track-transparent">
          <FRow label="Priority">
            <FMultiSel
              values={fv.priority || []}
              onChange={vals => setFv(f => ({ ...f, priority: vals }))}
              options={priorityOptions}
              placeholder="Choose Priority"
            />
          </FRow>

          <FRow label="Functional Categories">
            <FMultiSel
              values={fv.categories || []}
              onChange={vals => setFv(f => ({ ...f, categories: vals }))}
              options={categoryOptions}
              placeholder="Search & Choose Categories"
              searchable={true}
            />
          </FRow>

          <FRow label="Department">
            <FMultiSel
              values={fv.department || []}
              onChange={vals => setFv(f => ({ ...f, department: vals }))}
              options={deptOptions}
              placeholder="Search & Choose Department"
              searchable={true}
            />
          </FRow>

          <FRow label="Date Range" last>
            <div className="flex flex-col gap-[9px]">
              <div>
                <div className="text-[11px] text-[#94A3B8] font-medium mb-[5px]">From</div>
                <DInput value={fv.dateFrom || ""} onChange={e => setFv(f => ({ ...f, dateFrom: e.target.value }))} />
              </div>
              <div>
                <div className="text-[11px] text-[#94A3B8] font-medium mb-[5px]">To</div>
                <DInput value={fv.dateTo || ""} onChange={e => setFv(f => ({ ...f, dateTo: e.target.value }))} />
              </div>
            </div>
          </FRow>
        </div>

        {/* Footer */}
        <div className="flex gap-[10px] px-5 py-4 border-t border-[#F1F5F9] shrink-0">
          <button onClick={onClear}
            className="flex-1 py-[10px] bg-white border-[1.5px] border-[#EF4444] rounded-[10px] text-[13px] font-semibold text-[#EF4444] cursor-pointer hover:bg-[#FEF2F2] transition-colors duration-150">
            Clear Filter
          </button>
          <button onClick={onApply}
            className="flex-1 py-[10px] border-none bg-[#6366F1] rounded-[10px] text-[13px] font-semibold text-white cursor-pointer shadow-[0_2px_8px_rgba(99,102,241,.3)] hover:bg-[#4F46E5] transition-colors duration-150">
            Apply
          </button>
        </div>
      </div>
    </>
  );
}
